import React from 'react';
import { Chip, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AccountUser } from '../types/clientAccount';

// Chip de estado de un USUARIO de una cuenta de cliente, compartido por el
// portal del cliente y el detalle admin.
//
// El dato bueno viene del TENANT (must_change_password del enriquecimiento
// bulk /users-status, best-effort):
//   true  → «Pendiente de crear contraseña» (ámbar): el usuario existe pero
//           aún no ha entrado a fijar su contraseña.
//   false → «Activo».
// Sin dato (tenant lento o que aún no expone el endpoint) se cae al `status`
// LOCAL de la fila del marketplace — el mismo chip de siempre, nunca vacío.
interface AccountUserStatusChipProps {
  user: Pick<AccountUser, 'status' | 'must_change_password' | 'auth_methods'>;
  size?: 'small' | 'medium';
}

const AccountUserStatusChip: React.FC<AccountUserStatusChipProps> = ({ user, size = 'small' }) => {
  const { t } = useTranslation();
  const onlyGoogle =
    Array.isArray(user.auth_methods) &&
    user.auth_methods.length > 0 &&
    !user.auth_methods.includes('password');

  if (user.must_change_password === true) {
    return (
      <Tooltip
        title={t(
          'accountUsers.pendingPasswordHelp',
          'Ya puede entrar con el enlace que ha recibido por correo, pero todavía no ha creado su contraseña.'
        )}
      >
        <Chip
          label={t('accountUsers.pendingPassword', 'Pendiente de crear contraseña')}
          color="warning"
          size={size}
          sx={{ fontWeight: 600 }}
        />
      </Tooltip>
    );
  }

  if (user.must_change_password === false) {
    return (
      <Tooltip
        title={onlyGoogle
          ? t('accountUsers.googleOnlyHelp', 'Entra con su cuenta de Google (sin contraseña).')
          : ''}
      >
        <Chip
          label={t('accountUsers.active', 'Activo')}
          color="success"
          size={size}
          sx={{ fontWeight: 600 }}
        />
      </Tooltip>
    );
  }

  // Sin dato del tenant: estado local de la fila (active | pending | disabled).
  return (
    <Chip
      label={user.status === 'active' ? t('accountUsers.active', 'Activo') : user.status}
      color={user.status === 'active' ? 'success' : 'default'}
      size={size}
    />
  );
};

export default AccountUserStatusChip;
